import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { BehaviorSubject, finalize, map, Observable, of, switchMap } from "rxjs";
import { CreateNewsViewModel } from "../../core/model/DemoNews";
import { DemoNewsService } from "../../services/demo-news.service";

export interface UpdateNewsPopupData {
  title: string;
  id: number;
  hasPrev: boolean;
  hasNext: boolean;
  publishDate: Date;
}

interface NewsPopupData {
  title: string;
  id: number;
  publishDate: Date;
  hasPrev: boolean;
  hasNext: boolean;
  showGoToNews?: boolean;
  action: (currentId: number, action: "prev" | "next") => void;
  infoPopup$: Observable<UpdateNewsPopupData>;
}

@Component({
  selector: "brd-news-popup",
  templateUrl: "./news-popup.component.html",
  styleUrl: "./news-popup.component.less",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsPopupComponent implements OnInit {
  private newsSubject = new BehaviorSubject<number>(0);
  news$ = this.newsSubject.asObservable().pipe(
    switchMap(id => {
      const obs = id !== 0 ? this.newsService.getNewsPopup(id) : of({ id: 0, category: { name: "", color: "" } } as CreateNewsViewModel);
      return obs.pipe(
        finalize(() => {
          this.loading.set(false);
        })
      );
    }),
    map(res => {
      this.category.set({
        name: res.category?.name ?? "",
        color: res.category?.color ?? ""
      });
      return res;
    })
  );

  loading = signal(false);
  title = signal(this.data.title);
  publishDate = signal(this.data.publishDate);
  hasPrev = signal(this.data.hasPrev);
  hasNext = signal(this.data.hasNext);
  showGoToNewsButton = signal(this.data.showGoToNews ?? false);
  category = signal<{ name: string; color: string } | null>(null);
  private currentId = this.data.id;

  constructor(
    private dialogRef: MatDialogRef<NewsPopupData>,
    @Inject(MAT_DIALOG_DATA) public data: NewsPopupData,
    private readonly newsService: DemoNewsService,
    protected readonly router: Router
  ) {
    this.data.infoPopup$
      ?.pipe(
        map(res => {
          this.title.set(res.title);
          this.publishDate.set(new Date(res.publishDate));
          this.hasPrev.set(res.hasPrev);
          this.hasNext.set(res.hasNext);
          this.currentId = res.id;

          this.loadNews(res.id);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadNews(this.data.id);
  }

  prevClick(): void {
    this.loading.set(true);
    this.data.action(this.currentId, "prev");
  }

  nextClick(): void {
    this.loading.set(true);
    this.data.action(this.currentId, "next");
  }

  goToNews(): void {
    this.router.navigate(["/news"]).then(() => {
      this.dialogRef.close();
    });
  }

  private loadNews(id: number): void {
    this.loading.set(true);
    this.newsSubject.next(id);
  }
}
